// api/dispatchers/panelChartDispatcher.ts — per-symbol WS routing into the
// panelDataStore (design §4.5). After the Phase 3b cutover this is the SOLE
// chart-data dispatcher — the legacy global per-page chart store path was
// retired. It routes each ohlc/signal frame BY SYMBOL so every subscribed
// modular /chart panel ticks live.
//
// Routing contract (§4.5):
//   • broadcast-by-symbol — NO active-symbol guard. Any symbol with a live
//     panelDataStore refcount (store.isSubscribed(symbol)) receives the bar.
//     Unsubscribed symbols are dropped (the store also drops them defensively).
//   • candles: ONLY interval === 1 reach appendCandle. Higher TFs are derived
//     client-side via aggregateCandles from the single 1m base series, so the
//     server's higher-TF ohlc frames are intentionally ignored here.
//   • ohlc-snapshot: routed via seedFromSnapshot, which itself only seeds from
//     'idle'/'awaiting-seed' (NEW-3 precedence) — REST (setCandles) always wins.
//   • signals: keyed by symbol:interval into signalByKey via the typed setters.
//
// This dispatcher never returns a "handled" boolean — it runs as a parallel
// side-effect in messageDispatcher and must never throw on an unknown type.

import type { ServerToClientMessage } from '../types';
import { usePanelDataStore } from '../../stores/panelDataStore';
import { useSymbolStore, normalizeServerSymbol } from '../../stores/symbolStore';
import type { CandleData } from '../../stores/priceDataStore';

/** wsSymbol ("XRP/USD") → raw DB code ("XXRPZUSD"); panelDataStore keys by code. */
function toCode(symbol: string): string {
  return normalizeServerSymbol(symbol, useSymbolStore.getState().wsToCode);
}

/** symbol:interval key for the per-interval signal series. */
function intervalKey(code: string, interval: number): `${string}:${number}` {
  return `${code}:${interval}`;
}

/**
 * Chart-data message types this dispatcher OWNS. ohlc/ohlc-snapshot/ema/rsi are
 * actively routed; the remaining server-signal types still arrive over the WS but
 * are intentionally ignored (computed client-side via usePanelIndicators) — they are
 * listed here so messageDispatcher counts them as "handled" and does not DEV-warn.
 */
const CHART_DATA_TYPES = new Set<ServerToClientMessage['type']>([
  'ohlc', 'ohlc-snapshot', 'ema', 'rsi', 'sma', 'macd', 'bbands', 'vwap', 'adx',
  'atr', 'cmf', 'ichimoku', 'obv', 'psar', 'pivot', 'stoch',
  'williamsr', 'fvg', 'bos', 'choch', 'fib', 'svp',
]);

/**
 * Routes a WS message into panelDataStore, broadcasting to every subscribed
 * symbol. Returns true if the message type is a chart-data type this dispatcher
 * owns (so messageDispatcher treats it as handled). Must not throw — wrapped so a
 * malformed frame can never break the live chart path.
 */
export function dispatchPanelChartMessage(msg: ServerToClientMessage): boolean {
  try {
    routePanelChartMessage(msg);
  } catch {
    // Best-effort: a malformed frame is swallowed so it can't break dispatch.
  }
  return CHART_DATA_TYPES.has(msg.type);
}

function routePanelChartMessage(msg: ServerToClientMessage): void {
  const store = usePanelDataStore.getState();

  switch (msg.type) {
    case 'ohlc': {
      // Only the 1m base series feeds the per-symbol candle layer. Higher TFs
      // are derived at render via aggregateCandles, so ignore interval !== 1.
      if (msg.interval !== 1) return;
      const code = toCode(msg.symbol);
      if (!store.isSubscribed(code)) return;
      const candle: CandleData = {
        startTs: msg.data.startTs,
        open: msg.data.open,
        high: msg.data.high,
        low: msg.data.low,
        close: msg.data.close,
        volume: msg.data.volume,
        source: 'ws',
      };
      store.appendCandle(code, candle); // gate/buffer handled inside the store
      return;
    }

    case 'ohlc-snapshot': {
      if (msg.interval !== 1) return;
      const code = toCode(msg.symbol);
      if (!store.isSubscribed(code)) return;
      // seedFromSnapshot is itself a no-op unless loadState is idle/awaiting-seed
      // (NEW-3): REST (setCandles → 'ready') always wins, in-flight 'loading' is
      // preserved. We additionally short-circuit here so we never strip `source`
      // off candles for an already-ready entry.
      const entry = store.dataByKey[code];
      const ls = entry?.loadState;
      if (ls === 'ready' || ls === 'loading') return;
      const candles: CandleData[] = msg.candles.map((c) => ({
        startTs: c.startTs,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume,
        source: 'ws',
      }));
      store.seedFromSnapshot(code, candles);
      return;
    }

    case 'ema': {
      const code = toCode(msg.symbol);
      if (!store.isSubscribed(code)) return;
      store.addEma(intervalKey(code, msg.interval), msg.period, {
        startTs: msg.data.startTs,
        value: parseFloat(msg.data.value),
        period: msg.period,
      });
      return;
    }

    case 'rsi': {
      const code = toCode(msg.symbol);
      if (!store.isSubscribed(code)) return;
      store.addRsi(intervalKey(code, msg.interval), {
        startTs: msg.data.startTs,
        value: parseFloat(msg.data.value),
      });
      return;
    }

    // Only ema/rsi server signals are routed here. Every overlay indicator
    // (bbands/pivot/ichimoku/svp/fvg/bos/choch/fib/…) is computed CLIENT-SIDE
    // per panel from the 1m base via usePanelIndicators (useOverlayData), so
    // their server fanout frames are intentionally not consumed.
    default:
      return;
  }
}
